import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Text, Group, Button, Select, Stack, ActionIcon, Loader } from '@mantine/core';
import { Globe } from '@phosphor-icons/react';
import api from '../utils/api';
import { useNotifications } from '../hooks/useNotifications';

/**
 * Read + edit a Google Ads campaign's full geo targeting (cities & countries, each
 * with optional radius, plus EXCLUSIONS) in the campaign detail drawer.
 * Google Ads only.
 */
export default function CampaignLocationEditor({ campaignId, platform }) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [targets, setTargets] = useState([]);      // full targeting incl. exclusions
  const [presence, setPresence] = useState({});    // PRESENCE vs PRESENCE_OR_INTEREST
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [edit, setEdit] = useState([]);            // [{name, geo_id, radius_km, supports_radius, negative}]
  const [citySelectValue, setCitySelectValue] = useState(null);
  const [cityList, setCityList] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const cityTimer = useRef(null);

  useEffect(() => {
    if (platform !== 'google_ads') return;
    setLoading(true);
    api.get(`/ads/campaigns/${campaignId}/locations/`)
      .then(res => {
        setTargets(res.data.targets || []);
        setPresence(res.data.presence || {});
      })
      .catch(() => setError(t('campaignEdit.location.errorLoad')))
      .finally(() => setLoading(false));
  }, [campaignId, platform]);

  if (platform !== 'google_ads') return null;

  const fetchCities = (q) => {
    if (cityTimer.current) clearTimeout(cityTimer.current);
    if (!q || q.trim().length < 2) { setCityList([]); return; }
    cityTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/ads/geo/cities/?q=${encodeURIComponent(q.trim())}`);
        setCityList(res.data?.cities || []);
      } catch { setCityList([]); }
    }, 300);
  };

  const startEdit = () => {
    setEdit(targets.map(t => ({
      name: t.name,
      geo_id: t.geo_id || '',
      radius_km: t.radius_km ? String(t.radius_km) : '',
      // Proximity targets already carry a radius; area targets stay as areas unless
      // re-added from search (which tells us whether the city supports a radius).
      supports_radius: t.type === 'proximity',
      negative: !!t.negative,
    })));
    setEditing(true);
  };

  const addEntry = (opt) => {
    if (!opt) return;
    const key = opt.geo_id || `c:${opt.name}`;
    setEdit(prev => prev.some(c => (c.geo_id || `c:${c.name}`) === key) ? prev : [...prev, {
      name: opt.name, geo_id: opt.geo_id || '',
      supports_radius: !!opt.supports_radius,
      radius_km: opt.supports_radius ? '20' : '',
      negative: false,
    }]);
    setCitySelectValue(null); setCitySearch(''); setCityList([]);
  };
  const removeEntry = (i) => setEdit(prev => prev.filter((_, idx) => idx !== i));
  const updateEntry = (i, patch) => setEdit(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const save = async () => {
    if (!edit.some(c => !c.negative)) {
      addNotification(t('campaignEdit.location.needTarget'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        targets: edit.map(c => ({
          name: c.name, geo_id: c.geo_id,
          radius_km: (!c.negative && c.supports_radius && c.radius_km) ? Number(c.radius_km) : undefined,
          negative: !!c.negative,
        })),
      };
      await api.post(`/ads/campaigns/${campaignId}/locations/`, payload);
      // Refresh from the server so the read view reflects resolved names/types.
      const res = await api.get(`/ads/campaigns/${campaignId}/locations/`);
      setTargets(res.data.targets || []);
      setPresence(res.data.presence || {});
      setEditing(false);
      addNotification(t('campaignEdit.location.updated'), 'success');
    } catch (e) {
      addNotification(e.response?.data?.error || t('campaignEdit.location.errorUpdate'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const positives = targets.filter(t => !t.negative);
  const negatives = targets.filter(t => t.negative);
  const label = (t) => t.type === 'proximity' ? `${t.radius_km} km` : (t.name || t.geo_id);
  const presenceNote = presence.positive === 'PRESENCE'
    ? t('campaignEdit.location.presencePresence')
    : (presence.positive === 'PRESENCE_OR_INTEREST'
        ? t('campaignEdit.location.presenceInterest') : '');

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" mb={editing ? 'xs' : 0} wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Globe size={15} className="text-gray-500" />
          <Text size="sm" fw={600} c="dark">{t('campaignEdit.location.heading')}</Text>
        </Group>
        {!editing && !loading && (
          <Button size="compact-xs" variant="light" color="brand" onClick={startEdit}>{t('campaignEdit.location.edit')}</Button>
        )}
      </Group>

      {loading ? (
        <Group gap="xs" mt={6}><Loader size="xs" color="brand" /><Text size="xs" c="dimmed">{t('campaignEdit.location.loading')}</Text></Group>
      ) : error ? (
        <Text size="xs" c="red.6" mt={4}>{error}</Text>
      ) : editing ? (
        <>
          <Select
            placeholder={t('campaignEdit.location.addPlaceholder')}
            searchable clearable
            value={citySelectValue}
            searchValue={citySearch}
            onSearchChange={(v) => { setCitySearch(v); fetchCities(v); }}
            data={cityList.map(c => ({ value: c.geo_id || `c:${c.name}`, label: c.display || c.name }))}
            filter={({ options }) => options}
            nothingFoundMessage={citySearch.trim().length >= 2 ? t('campaignEdit.location.noResults') : t('campaignEdit.location.minChars')}
            onChange={(val) => addEntry(cityList.find(c => (c.geo_id || `c:${c.name}`) === val))}
          />
          {edit.length > 0 && (
            <Stack gap={6} mt="sm">
              {edit.map((c, i) => (
                <Paper key={c.geo_id || `c:${c.name}`} withBorder p="xs" radius="md">
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}
                      c={c.negative ? 'red.6' : 'dark'}>
                      {c.negative ? t('campaignEdit.location.excludePrefix') : ''}{c.name}
                    </Text>
                    {!c.negative && c.supports_radius && (
                      <Select size="xs" w={104} value={c.radius_km}
                        onChange={(v) => updateEntry(i, { radius_km: v })}
                        data={[
                          { value: '5', label: '5 km' }, { value: '10', label: '10 km' },
                          { value: '20', label: '20 km' }, { value: '30', label: '30 km' },
                          { value: '50', label: '50 km' },
                        ]} />
                    )}
                    {!c.negative && !c.supports_radius && (
                      <Text size="10px" c="dimmed">{t('campaignEdit.location.wholeArea')}</Text>
                    )}
                    <Button size="compact-xs"
                      variant={c.negative ? 'filled' : 'light'}
                      color={c.negative ? 'red' : 'gray'}
                      onClick={() => updateEntry(i, { negative: !c.negative })}>
                      {c.negative ? t('campaignEdit.location.excluded') : t('campaignEdit.location.exclude')}
                    </Button>
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => removeEntry(i)}>×</ActionIcon>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
          <Group gap="xs" mt="sm">
            <Button size="xs" color="brand" loading={saving} onClick={save}>{t('campaignEdit.location.save')}</Button>
            <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(false)}>{t('campaignEdit.location.cancel')}</Button>
          </Group>
        </>
      ) : (
        <div style={{ marginTop: 4 }}>
          <Text size="xs" c="dimmed">
            {positives.length
              ? positives.map(label).join(', ')
              : t('campaignEdit.location.noLocations')}
          </Text>
          {negatives.length > 0 && (
            <Text size="xs" c="red.6" mt={2} fw={500}>
              {t('campaignEdit.location.excludePrefix')}{negatives.map(label).join(', ')}
            </Text>
          )}
          {presenceNote && <Text size="10px" c="dimmed" mt={2}>{presenceNote}</Text>}
        </div>
      )}
    </Paper>
  );
}
